import { useState, useMemo } from "react";
import { useMediaQuery } from "@reactuses/core";
import { useFetch } from "../hooks/api";
import { Col, Container as Grid, NavDropdown, Row } from "react-bootstrap";
import { Accordion } from "../components/core/basic/Accordion";
import { useParams } from "@tanstack/react-router";
import DownloadReport from "../modules/perf_reports/DownloadReport";
import { DatesProvider } from "../dates/DatesContext";
import { useDates } from "../dates/DatesContext";
import { useDateFormatter } from "../i18n/useDateFormatter";
import * as Sentry from "@sentry/react";

import BreadcrumbDates from "../components/core/blocks/BreadcrumbDates";
import Loading from "../components/core/blocks/Loading";
import getMetricSpec from "../modules/merchandising/utils/metrics";
import AdPlanModal from "../components/core/blocks/AdPlan/AdPlanModal";
import { Button } from "../components/core/basic/Button";
import MetricsCard from "../components/custom/merchandise/MetricsCard";
import { AD_CREATION_FAILED_STATUS, MODERATION_STATUSES, AD_STATES } from "../components/core/blocks/AdPlan/constants";

import useCategories from "../hooks/useCategories";
import { AGENCY_DASHBOARD_BREADCRUMB_SPEC, DASHBOARD_LINKS } from "../utils/dashboardLinks";
import { CategoryProducts } from "../modules/advertising/components/CategoryProducts";
import DetailedMetric from "../components/core/blocks/DetailedMetric";
import AdvertisementsTable from "../modules/advertising/components/AdvertisementsTable";
import { AnnotationsTable } from "../components/custom/growth/AnnotationsTable";
import AdTypeSection, { adTypesByChannel } from "../modules/advertising/components/adTypeSection/AdTypeSection";
import { AD_TYPES } from "../components/core/blocks/AdPlan/constants";
import { useAppSelector } from "../redux/hooks";
import { keepPreviousData } from "@tanstack/react-query";
import DashboardSelector from "../modules/advertising/components/DashboardSelector";
import { useSimpleCan } from "../config/casl/casl";
import { useTranslation } from "react-i18next";
import { useResolvedLabels } from "../i18n/useResolvedLabels";

export const getDashboardTo = ({ type, id }) => {
  let to = ``;

  const query = {};

  if (!id) {
    to = `/user/advertising/dashboard`;
  } else {
    if (type === "category") {
      to = `/user/advertising/dashboard/category/${id}`;
    }

    if (type === "advertisement") {
      to = `/user/advertising/dashboard/ads/${id}`;
    }

    if (type === "listing") {
      to = `/user/advertising/dashboard/listings/${id}`;
      query.tab = "details";
    }
  }

  const params = new URLSearchParams(query);
  if (params.toString()) {
    to = `${to}?${params.toString()}`;
  }

  return to;
};

const Dashboard = () => {
  const marketPlace = useAppSelector((state) => state.marketPlace);
  const user = useAppSelector((state) => state.user);
  const channel = useAppSelector((state) => state.channel.currentChannel);
  const { t } = useTranslation(["advertising"]);

  const userCanExportAds = useSimpleCan("export", "Ad");
  const { id: categoryId } = useParams({ from: "/_authenticated/user/advertising/dashboard/category/$id" });

  const { currentCategoryDetails: categoryDetails, categories: categoryData } = useCategories(
    categoryId,
    "advertising/dashboard",
  );
  const [adTypeFilter, setAdTypeFilter] = useState({
    label: t(($) => $.advertising["All Media Plan Types"]),
    value: "all",
  });
  // Resolve `adTypesByChannel[channel]` once so the dropdown options below — and the
  // option stored in `adTypeFilter` when the user picks one — carry plain strings
  // instead of selector functions. Mirrors the AdvertisingDashboard fix.
  const resolvedAdTypes = useResolvedLabels(adTypesByChannel[channel] ?? []);
  const [adPlanOpen, setAdPlanOpen] = useState(false);

  const smallViewportWidthTable = useMediaQuery("(max-width:1550px)");

  const { group, start, end, preStart, preEnd } = useDates();
  const longDate = useDateFormatter("long");

  const filters = {
    mp: marketPlace.marketPlace,
    date_group: group,
    start_date: start.format("YYYY-MM-DD"),
    end_date: end.format("YYYY-MM-DD"),
    pre_start_date: preStart.format("YYYY-MM-DD"),
    pre_end_date: preEnd.format("YYYY-MM-DD"),
    channel: channel,
    category: categoryId,
    group_by: "ad_id",
    empty: true,
  };

  const { isSuccess, data, isError, isLoading, isFetching } = useFetch(
    ["category_report", marketPlace.marketPlace, start, end, preStart, preEnd, group, categoryId],
    `/api/data_report/category_report`,
    { ...filters, group_by: "category_id" },
    {
      select: (d) => d.data,
      placeholderData: keepPreviousData,
    },
  );

  const { data: categorySummaryData, isLoading: isAdDataLoading } = useFetch(
    ["ad_report", marketPlace.marketPlace, start, end, preStart, preEnd, group, categoryId],
    `/api/data_report/ad_report`,
    { ...filters },
    {
      select: (d) => d.data,
      placeholderData: keepPreviousData,
      default: [],
    },
  );

  const { data: annotationData, isLoading: isAnnotationDataLoading } = useFetch(
    ["annotations", start, end, channel, categoryId, marketPlace.marketPlace],
    "/api/annotations/",
    {
      groupby: group,
      ts_after: `${filters["start_date"]}T00:00:00`,
      ts_before: `${filters["end_date"]}T23:59:59`,
      category: categoryId,
      marketplace: marketPlace.marketPlace,
      channel: channel === "amazon" ? "AMZ" : channel,
    },
    // ts_before and ts_after need values from categories to contain start and end date within query string
    {
      placeholderData: keepPreviousData,
      enabled: !!categoryId,
    },
  );

  const seasonalCategoryQuery = useFetch(
    ["seasonality", categoryId],
    `/api/seasonality`,
    { category: parseInt(categoryId) },
    {
      select: (res) => res.data?.results ?? [],
      enabled: !!categoryId,
    },
  );
  let isSeasonalCategory = false;
  if (seasonalCategoryQuery.isSuccess) {
    try {
      isSeasonalCategory = JSON.parse(seasonalCategoryQuery.data[0]?.weekly_seasons ?? "[]")?.some(
        (day) => day.multiplier !== 1,
      );
    } catch (e) {
      Sentry.captureException(e);
    }
  }

  const { overviewMetrics } = useMemo(() => {
    const acosOrRoas = user?.preferences?.acos_or_roas ?? "acos";
    const roasMetrics = [getMetricSpec("roas"), getMetricSpec("troas")];
    const acosMetrics = [getMetricSpec("acos"), getMetricSpec("total_acos")];
    return {
      overviewMetrics: {
        metrics: [
          getMetricSpec("sales"),
          getMetricSpec("ad_sales"),
          getMetricSpec("ad_spend"),
          ...(acosOrRoas === "acos" ? acosMetrics : roasMetrics),
          ...(acosOrRoas === "acos" ? roasMetrics : acosMetrics),
          getMetricSpec("clicks"),
          getMetricSpec("impressions"),
          getMetricSpec("orders"),
          getMetricSpec("cpc"),
          getMetricSpec("conversion_rate"),
          getMetricSpec("ctr"),
        ],
      },
    };
  }, [user]);

  const breadCrumbItems = [
    AGENCY_DASHBOARD_BREADCRUMB_SPEC,
    {
      name: t(($) => $.advertising["Advertising Dashboard"]),
      href: getDashboardTo({
        type: "category",
        id: null,
      }),
      dropdownLinkOptions: DASHBOARD_LINKS,
      searchable: false,
      unsorted: true,
      dropdownSubtitle: t(($) => $.advertising["Dashboards"]),
    },
  ];

  if (categoryId) {
    breadCrumbItems.push({
      name: categoryDetails?.leaf_category_name,
      href: categoryDetails.href,
      dropdownLinkOptions: categoryData,
      searchable: true,
      searchPlaceholderText: t(($) => $.advertising["Search categories..."]),
      dropdownSubtitle: t(($) => $.advertising["Categories"]),
    });
  }

  const adPlans = useMemo(() => {
    return (
      categorySummaryData?.adplans
        ?.filter((adplan) => {
          if (adTypeFilter?.value === "all") {
            return true;
          }
          if (AD_TYPES.SPONSORED_BRAND_V2.value === adTypeFilter?.value) {
            return [
              AD_TYPES.SPONSORED_BRAND.value,
              AD_TYPES.SPONSORED_BRAND_V2.value,
              AD_TYPES.SPONSORED_BRAND_WALMART.value,
            ].includes(adplan.ad_type);
          }
          if (AD_TYPES.SPONSORED_DISPLAY_V2.value === adTypeFilter?.value) {
            return [AD_TYPES.SPONSORED_DISPLAY.value, AD_TYPES.SPONSORED_DISPLAY_V2.value].includes(adplan.ad_type);
          }
          if (AD_TYPES.SPONSORED_BRAND_WALMART.value === adTypeFilter?.value) {
            return [AD_TYPES.SPONSORED_BRAND_WALMART.value, AD_TYPES.SPONSORED_BRAND_VIDEO_WALMART.value].includes(
              adplan.ad_type,
            );
          }

          return adplan.ad_type === adTypeFilter?.value;
        })
        ?.map((adplan) => ({
          ...adplan,
          id: adplan.ad_id,
          category: categoryId,
          ad_spend: adplan.cost,
        })) ?? []
    );
  }, [categorySummaryData, categoryId, adTypeFilter]);

  return (
    <>
      <DashboardSelector />
      <Grid
        fluid
        style={{
          background: "#fefefe",
          minHeight: "calc(100vh - 150px)",
        }}
      >
        <Row>
          <Col xs={12} className="mb-4">
            <BreadcrumbDates items={breadCrumbItems} />
          </Col>
        </Row>
        <Row>
          <Col xs={12}>
            {isLoading && <Loading />}
            {isError && <p>{t(($) => $.advertising["Error Loading Dashboard"])}</p>}
            {isSuccess && (
              <Row style={{ marginBottom: "1rem" }}>
                <Col xs={12}>
                  <MetricsCard
                    id={"advertingOverview"}
                    title={categoryDetails?.leaf_category_name}
                    totals={{
                      data: data?.total,
                      loading: isFetching,
                    }}
                    timeline={{
                      data: data?.date || [],
                      loading: isFetching,
                    }}
                    metrics={overviewMetrics.metrics}
                    showMetricsSelector
                    initialSelectedMetric={["ad_sales", "ad_spend", "roas"]}
                    group={group}
                    showAnnotations={true}
                    selectedCategory={categoryId}
                    byline={
                      <>
                        {longDate.format(start.toDate())}
                        {" - "}
                        {longDate.format(end.toDate())}
                        {isLoading && <i className="fa fa-spinner fa-spin"></i>}
                      </>
                    }
                    context={"category"}
                  >
                    <Row>
                      <Col xs={12} lg={6} className="text-start d-flex">
                        <div className="px-5">
                          <DetailedMetric
                            center
                            metric={{
                              id: "budget",
                              name: t(($) => $.advertising["Monthly Budget"]),
                              format: "currency",
                              decimalPlaces: 0,
                              accessor: (data) => data.value,
                            }}
                            data={{
                              value: categorySummaryData?.adplans?.reduce((acc, adplan) => {
                                if (
                                  ([AD_STATES.ACTIVE.value].includes(adplan.state) ||
                                    adplan.ad_status === MODERATION_STATUSES.PENDING.value) &&
                                  adplan.ad_status !== AD_CREATION_FAILED_STATUS
                                ) {
                                  return acc + adplan.monthly_budget;
                                }
                                return acc;
                              }, 0),
                            }}
                            showTarget={false}
                          />
                        </div>
                        <div className="px-5">
                          <DetailedMetric
                            center
                            metric={{
                              id: "budget",
                              name: t(($) => $.advertising["Daily Budget"]),
                              format: "currency",
                              decimalPlaces: 0,
                              accessor: (data) => data.value,
                            }}
                            data={{
                              value: categorySummaryData?.adplans?.reduce((acc, adplan) => {
                                if (
                                  ([AD_STATES.ACTIVE.value].includes(adplan.state) ||
                                    adplan.ad_status === MODERATION_STATUSES.PENDING.value) &&
                                  adplan.ad_status !== AD_CREATION_FAILED_STATUS
                                ) {
                                  return acc + adplan.daily_budget;
                                }
                                return acc;
                              }, 0),
                            }}
                            showTarget={false}
                          />
                        </div>
                      </Col>
                      <Col xs={12} lg={6} className="d-flex justify-content-end align-items center mx-auto">
                        <p className="d-flex justify-content-end align-items center my-auto">
                          <Button onPress={() => setAdPlanOpen(true)}>
                            {t(($) => $.advertising["Create A New Media Plan"])}
                          </Button>
                          <AdPlanModal
                            isOpen={adPlanOpen}
                            onClose={() => setAdPlanOpen(false)}
                            dashboard={false}
                            categories={categoryData}
                            categoryId={categoryId}
                          />
                        </p>
                      </Col>
                    </Row>
                    <Col xs={12}>
                      <Accordion title={t(($) => $.advertising["Ad Types"])}>
                        <Row>
                          <Col xs={12}>
                            <AdTypeSection adTypeFilter={adTypeFilter} setAdTypeFilter={setAdTypeFilter} />
                          </Col>
                        </Row>
                      </Accordion>
                    </Col>

                    <Col xs={12}>
                      <Accordion
                        title={
                          adTypeFilter?.value === "all" ?
                            t(($) => $.advertising["Media Plans"])
                          : t(($) => $.advertising["{adType} Media Plans"], { adType: adTypeFilter?.label })
                        }
                        defaultExpanded={true}
                      >
                        <Row>
                          <Col xs={12}>
                            <AdvertisementsTable
                              stats={adPlans}
                              showTabs={true}
                              marketPlace={marketPlace}
                              channel={channel}
                              isLoading={isAdDataLoading}
                              smallViewportWidthTable={smallViewportWidthTable}
                              categoryDetails={{
                                ...categoryDetails,
                                seasonalCategory: isSeasonalCategory,
                              }}
                              leftActionButton={
                                <div
                                  style={{
                                    width: "400px",
                                    marginRight: "auto",
                                  }}
                                  className="me-auto"
                                >
                                  <NavDropdown
                                    title={adTypeFilter?.label}
                                    className={"fs-standard"}
                                    style={{
                                      minWidth: "200px",
                                      fontSize: "14px",
                                      fontWeight: 400,
                                    }}
                                  >
                                    {[
                                      {
                                        label: t(($) => $.advertising["All Media Plan Types"]),
                                        value: "all",
                                      },
                                      ...resolvedAdTypes.filter(
                                        (type) =>
                                          ![
                                            AD_TYPES.SPONSORED_BRAND.value,
                                            AD_TYPES.SPONSORED_DISPLAY.value,
                                            AD_TYPES.SPONSORED_BRAND_VIDEO.value,
                                          ].includes(type?.value),
                                      ),
                                    ]?.map((option, index) => (
                                      <NavDropdown.Item
                                        key={index}
                                        title={adTypeFilter?.label}
                                        onClick={() => setAdTypeFilter(option)}
                                      >
                                        {option.label}
                                      </NavDropdown.Item>
                                    ))}
                                  </NavDropdown>
                                </div>
                              }
                            />
                          </Col>
                          {userCanExportAds && (
                            <Col xs={12} className="text-right">
                              <DownloadReport
                                url_path={"ad_report"}
                                filters={{ ...filters, empty: false }}
                                report_dl={"ads"}
                              />
                            </Col>
                          )}
                        </Row>
                      </Accordion>
                      <Accordion title={t(($) => $.advertising["Products"])}>
                        <CategoryProducts
                          categoryId={categoryId}
                          marketPlace={marketPlace}
                          smallViewportWidthTable={smallViewportWidthTable}
                          channel={channel}
                        />
                      </Accordion>

                      <Accordion title={t(($) => $.advertising["Impact Analysis"])}>
                        <Row>
                          <Col xs={12}>
                            <AnnotationsTable
                              data={annotationData?.data}
                              isLoading={isAnnotationDataLoading}
                              annotationType={"advertisement"}
                            />
                          </Col>
                        </Row>
                      </Accordion>
                    </Col>
                  </MetricsCard>
                </Col>
              </Row>
            )}
          </Col>
        </Row>
      </Grid>
    </>
  );
};

const AdvertisingOverview = () => {
  return (
    <DatesProvider>
      <Dashboard />
    </DatesProvider>
  );
};

export default AdvertisingOverview;
